import { Action } from '../actions/ActionInterface';
import { ActionTypes } from '../actions/ActionTypes';
import { Beer } from '../types';

export interface BeerDataState {
  beers: Beer[];
  loading: boolean;
  error: Error | null;
}

const initialState: BeerDataState = {
  beers: [],
  loading: false,
  error: null,
};

/**
 * Beer data reducer
 * Handles all actions related to beer data (fetching, updating)
 * Following Single Responsibility Principle by focusing only on beer data
 */
export function beerDataReducer(state: BeerDataState = initialState, action: Action): BeerDataState {
  // Use the action's execute method if available (Command pattern)
  // Check if execute exists without causing TypeScript errors
  if (action && typeof (action as any).execute === 'function') {
    // The execute method expects the full state, so we need to adapt it
    const fullState = { beers: state.beers, loading: state.loading, error: state.error } as any;
    try {
      const newState = action.execute(fullState);
    
      // Extract only the properties this reducer is responsible for
    const result = {
      beers: newState.beers || state.beers,
      loading: newState.loading !== undefined ? newState.loading : state.loading,
      error: newState.error !== undefined ? newState.error : state.error,
    };
    return result;
    } catch (error) {
      console.error('[beerDataReducer] Error executing action.execute:', error);
      return state;
    }
  }
  
  // Fallback to traditional switch statement for backward compatibility
  switch (action.type) {
    case ActionTypes.FETCH_BEERS_START:
      return {
        ...state,
        loading: true,
        error: null,
      };
      
    case ActionTypes.FETCH_BEERS_SUCCESS:
      const successResult = {
        ...state,
        beers: action.payload || [],
        loading: false,
        error: null,
      };
      return successResult;
      
    case ActionTypes.FETCH_BEERS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload || new Error('Unknown error'),
      };
      
    case ActionTypes.UPDATE_BEER_PROPERTY: {
      const { beerId, property, value } = action.payload || { beerId: '', property: 'id', value: null };
      
      if (!beerId || !property) {
        return state;
      }
      
      const updatedBeers = state.beers.map(beer => {
        if (beer.id === beerId) {
          return {
            ...beer,
            [property]: value,
          };
        }
        return beer;
      });
      
      return {
        ...state,
        beers: updatedBeers,
      };
    }
      
    default:
      return state;
  }
}
